import { t } from '../theme';
import { useRuleFor, useStore } from '../store';

/**
 * "Immer vorrätig: 2 ×".
 *
 * One number, not a trigger/condition builder — empty and low-stock are the
 * same question with a different value, and a rule builder would let people
 * express things they will never want at the cost of making the common case a
 * form.
 *
 * The rule outlives the item on purpose: it keys on the product, because the
 * last carton is deleted the moment it runs out, which is exactly when the
 * rule has to still exist.
 */
export function StockRule({ name, barcode }: { name: string; barcode: string | null }) {
  const rule = useRuleFor(name);
  const setRule = useStore((s) => s.setRule);
  const updateRule = useStore((s) => s.updateRule);
  const clearRule = useStore((s) => s.clearRule);
  const flash = useStore((s) => s.flash);

  const on = Boolean(rule);

  const toggle = async () => {
    try {
      if (rule) {
        await clearRule(rule.id);
      } else {
        await setRule(name, 1, barcode);
        flash(`„${name}" wird nachgekauft, wenn er ausgeht`);
      }
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Regel konnte nicht gespeichert werden');
    }
  };

  const step = (delta: number) => {
    if (!rule) return;
    const next = Math.max(1, Math.min(99, rule.minQty + delta));
    if (next !== rule.minQty) void updateRule(rule.id, next);
  };

  return (
    <div
      style={{
        borderRadius: 20,
        background: on ? 'oklch(0.27 0.012 60)' : t.surfaceSunken,
        border: on ? `1px solid ${t.accent}` : t.hairline,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        minWidth: 0,
      }}
    >
      <div className="press" onClick={() => void toggle()} style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: 52,
            height: 32,
            flex: 'none',
            borderRadius: 999,
            background: on ? t.accent : 'oklch(0.34 0.012 60)',
            display: 'flex',
            alignItems: 'center',
            padding: 3,
            justifyContent: on ? 'flex-end' : 'flex-start',
            transition: 'background 140ms',
          }}
        >
          <div style={{ width: 26, height: 26, borderRadius: 999, background: on ? t.accentInk : 'oklch(0.62 0.012 60)' }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 500 }}>Immer vorrätig</div>
          <div style={{ fontSize: 15, color: t.inkFaint, marginTop: 2, textWrap: 'pretty' }}>
            {on
              ? `Kommt auf die Einkaufsliste, sobald weniger als ${rule!.minQty} da ${rule!.minQty === 1 ? 'ist' : 'sind'}`
              : 'Automatisch nachkaufen, wenn es ausgeht'}
          </div>
        </div>
      </div>

      {on && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          <div className="press" onClick={() => step(-1)} style={stepBtn}>−</div>
          <div style={{ minWidth: 46, textAlign: 'center', fontSize: 23, fontWeight: 600 }}>
            {rule!.minQty}×
          </div>
          <div className="press" onClick={() => step(1)} style={stepBtn}>+</div>
        </div>
      )}
    </div>
  );
}

const stepBtn: React.CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 14,
  background: t.control,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 500,
};
